import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

/** Type dérivé côté backend (offerId != null), jamais persisté -- voir PreventionCampaignService (waitora-platform). */
export type CampaignType = 'PROMOTE_OFFER' | 'TEST_INTEREST';

export type CampaignStatus = 'SCHEDULED' | 'ACTIVE' | 'ENDED';

export type CampaignScope = 'ALL' | 'SITES';

/**
 * État opérationnel réel (C6, distinct du statut administratif {@link CampaignStatus} -- jamais
 * fusionnés, voir `PREVENTION_CAMPAIGN_OPERATIONAL_INTEGRITY_C6_DESIGN.md`) : capacité RÉELLE de la
 * campagne à être diffusée, dérivée côté backend de {@code CampaignOfferPolicy}, jamais recalculée
 * ici. `NOT_APPLICABLE` couvre à la fois une campagne `TEST_INTEREST` (aucune Offer associée, rien à
 * évaluer) et une campagne `SCHEDULED`/`ENDED` (la déliverabilité n'a de sens que pour `ACTIVE`).
 */
export type DeliverabilityStatus = 'DELIVERABLE' | 'PARTIALLY_BLOCKED' | 'BLOCKED' | 'NOT_APPLICABLE';

/**
 * Motif de blocage (backend `CampaignOfferRefusal`, lots C2/C3) -- jamais affiché brut au
 * laboratoire, toujours traduit via `commPrevention.form.deliverability.reason.<code>` (voir
 * {@code resolveDeliverabilityReasonLabel} dans `comm-prevention-form.component.ts`).
 */
export type CampaignOfferRefusalCode =
  | 'OFFER_NOT_FOUND'
  | 'OFFER_INACTIVE'
  | 'OFFER_INCOMPLETE'
  | 'TOPIC_UNAVAILABLE'
  | 'CATALOG_OFFER_ON_CLINICAL_TOPIC'
  | 'EXPLICIT_OFFER_ON_CLINICAL_TOPIC'
  | 'CATALOG_OFFER_CONTENT_NOT_PUBLISHED'
  | 'CATALOG_OFFER_ADDITIONAL_ITEM_NOT_ALLOWED'
  | 'OFFER_UNAVAILABLE_ON_SITE';

export interface CampaignListRow {
  campaignId: string;
  subject: string;
  titre: string;
  type: CampaignType;
  siteCount: number;
  startsAt: string | null;
  endsAt: string | null;
  status: CampaignStatus;
  offerId: string | null;
  offerName: string | null;
  createdAt: string;
  /** C6 -- absent/`undefined` sur une réponse backend antérieure au lot : traité comme
   *  `NOT_APPLICABLE` (aucun avertissement), jamais une erreur. Jamais recalculé côté frontend. */
  deliverabilityStatus?: DeliverabilityStatus;
  /** C6 -- motif global (`BLOCKED` uniquement), `null`/absent sinon. */
  deliverabilityReason?: CampaignOfferRefusalCode | null;
  /** C6 -- nombre de sites bloqués (`PARTIALLY_BLOCKED` uniquement), pour le compteur en liste
   *  sans devoir charger le détail complet. */
  blockedSiteCount?: number;
}

export interface CampaignSite {
  establishmentId: string;
  name: string;
}

export interface CampaignDetail {
  campaignId: string;
  subject: string;
  titre: string;
  texte: string;
  imageData: string | null;
  type: CampaignType;
  offerId: string | null;
  offerName: string | null;
  sites: CampaignSite[];
  startsAt: string | null;
  endsAt: string | null;
  status: CampaignStatus;
  createdAt: string;
  /** C6 -- voir {@link CampaignListRow.deliverabilityStatus}, même contrat, même discipline de
   *  rétrocompatibilité (absent = `NOT_APPLICABLE`). */
  deliverabilityStatus?: DeliverabilityStatus;
  deliverabilityReason?: CampaignOfferRefusalCode | null;
  blockedSiteCount?: number;
  /** C6 -- identifiants des sites bloqués (`PARTIALLY_BLOCKED` uniquement) -- leur nom est retrouvé
   *  côté frontend depuis {@link sites} (déjà chargé), jamais une seconde source de vérité. */
  blockedEstablishmentIds?: string[];
}

export interface EligibleSite {
  establishmentId: string;
  name: string;
  eligible: boolean;
}

export interface CreateCampaignRequest {
  subject: string;
  titre: string;
  texte: string;
  imageData?: string | null;
  offerId?: string | null;
  scope: CampaignScope;
  establishmentIds?: string[];
  startsAt?: string | null;
  endsAt?: string | null;
}

export interface UpdateCampaignRequest {
  subject: string;
  titre: string;
  texte: string;
  imageData?: string | null;
  offerId?: string | null;
  /** Lot "Sites de diffusion modifiables" (2026-09-09) -- au moins un site requis (validé aussi côté backend). */
  establishmentIds: string[];
  startsAt?: string | null;
  endsAt?: string | null;
}

/**
 * "Comm prévention V1" (Lot 2026-09-08) -- création/planification/suspension des campagnes de
 * prévention. Distinct de {@code PatientDemandService}/l'écran analytics existant "Prévention >
 * Campagne de prévention" (mesurer/piloter, non touché par ce lot) : ce service couvre
 * créer/diffuser/planifier uniquement.
 */
@Injectable({ providedIn: 'root' })
export class PreventionCampaignsService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/api/v1/prevention/campaigns`;

  listCampaigns(): Observable<CampaignListRow[]> {
    return this.http.get<CampaignListRow[]>(this.baseUrl);
  }

  getCampaign(campaignId: string): Observable<CampaignDetail> {
    return this.http.get<CampaignDetail>(`${this.baseUrl}/${campaignId}`);
  }

  createCampaign(request: CreateCampaignRequest): Observable<CampaignDetail> {
    return this.http.post<CampaignDetail>(this.baseUrl, request);
  }

  updateCampaign(campaignId: string, request: UpdateCampaignRequest): Observable<CampaignDetail> {
    return this.http.put<CampaignDetail>(`${this.baseUrl}/${campaignId}`, request);
  }

  suspendCampaign(campaignId: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${campaignId}/suspend`, {});
  }

  /**
   * {@code offerId} omis : tous les sites de l'organisation, tous éligibles ("Tester l'intérêt").
   * {@code subject} (C6) : optionnel, transmis tel quel -- le backend reste seul juge de
   * l'éligibilité par sujet (topic clinique, etc.), jamais une règle recalculée ici.
   */
  listEligibleSites(offerId?: string | null, subject?: string | null): Observable<EligibleSite[]> {
    let params = new HttpParams();
    if (offerId) params = params.set('offerId', offerId);
    if (subject) params = params.set('subject', subject);
    return this.http.get<EligibleSite[]>(`${this.baseUrl}/eligible-sites`, { params });
  }
}
